import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, LayoutDashboard, Settings, Mic, Upload, FileCheck, ChevronDown, Video, FolderOpen, MessageSquare, Users, UserCog, Gauge, SlidersHorizontal, Building2, KeyRound, Bot, BookOpen, PlugZap, Link2, Store, CreditCard, Coins } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { HealthStatus } from "./HealthStatus";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useTenant } from "@/hooks/useTenant";
import { getBrandConfig } from "@/config/brand";
import { MARKETING_SITE_URL } from "@/lib/constants";
import { getTenantSubdomain } from "@/lib/tenant";

export const Navigation = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const { data: tenant } = useTenant();
  const brand = getBrandConfig();
  const canManageCompany = Boolean(user?.is_admin || user?.is_company_admin);
  const canManageDeveloper = Boolean(user?.is_admin || user?.is_developper);
  const tenantSubdomain = getTenantSubdomain(window.location.hostname);

  const handleLogout = () => {
    void logout();
  };

  const isMeetingsActive = ["/meetings", "/upload", "/record", "/meeting-bot", "/meeting-settings"].some(path =>
    location.pathname.startsWith(path) || location.pathname.startsWith("/meeting")
  );

  if (isAuthenticated) {
    return null;
  }

  return (
      <nav className="sticky top-0 z-50 border-b border-border/40 glass">
        <div className="container mx-auto flex h-20 items-center justify-between px-4">
          <a href={MARKETING_SITE_URL} className="flex items-center gap-4 lg:gap-6 group">
            <img
              src={brand.paths.logo}
              alt={brand.logoAlt}
              className="h-14 w-auto object-contain transition-opacity group-hover:opacity-80"
            />
            <div className="hidden flex-col md:flex">
              <span className="font-mono font-bold text-2xl tracking-tight">{brand.brandNameShort}</span>
              {tenant && (
                <span className="text-xs text-muted-foreground">
                  {t("layout.forCompany", { company: tenant.company_name })}
                </span>
              )}
            </div>
          </a>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <HealthStatus variant="icon" />
            {isAuthenticated ? (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant={location.pathname.startsWith("/ai-agents") ? "secondary" : "ghost"}
                      size="sm"
                      className="gap-1"
                    >
                      <MessageSquare className="h-4 w-4" />
                      <span className="hidden md:inline">{t("nav.aiAgents")}</span>
                      <ChevronDown className="h-3 w-3 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-52">
                    <DropdownMenuItem asChild>
                      <Link to="/ai-agents" className="cursor-pointer">
                        <MessageSquare className="mr-2 h-4 w-4" />
                        {t("nav.aiAgents")}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/ai-agents/insights" className="cursor-pointer">
                        <Gauge className="mr-2 h-4 w-4" />
                        {t("nav.staffInsights")}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/ai-agents?agent_menu=skills" className="cursor-pointer">
                        <BookOpen className="mr-2 h-4 w-4" />
                        {t("nav.skills")}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/ai-agents?agent_menu=plugins" className="cursor-pointer">
                        <PlugZap className="mr-2 h-4 w-4" />
                        {t("nav.plugins")}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/ai-agents?agent_menu=connectors" className="cursor-pointer">
                        <Link2 className="mr-2 h-4 w-4" />
                        {t("nav.connectors")}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/ai-agents?agent_menu=marketplace" className="cursor-pointer">
                        <Store className="mr-2 h-4 w-4" />
                        {t("nav.marketplace")}
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

              <Button
                variant={location.pathname === "/documents" ? "secondary" : "ghost"}
                size="sm"
                asChild
              >
                <Link to="/documents" className="gap-2">
                  <FolderOpen className="h-4 w-4" />
                  <span className="hidden md:inline">{t("nav.documents")}</span>
                </Link>
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={isMeetingsActive ? "secondary" : "ghost"}
                    size="sm"
                    className="gap-1"
                  >
                    <Video className="h-3 w-3" />
                    <span className="hidden md:inline">{t("nav.meetings")}</span>
                    <ChevronDown className="h-3 w-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/meetings" className="cursor-pointer">
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      {t("nav.allMeetings")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/upload" className="cursor-pointer">
                      <Upload className="mr-2 h-4 w-4" />
                      {t("nav.uploadAudio")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/record" className="cursor-pointer">
                      <Mic className="mr-2 h-4 w-4" />
                      {t("nav.recordMeeting")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/meeting-bot" className="cursor-pointer">
                      <Bot className="mr-2 h-4 w-4" />
                      {t("nav.meetingBot")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/meeting-settings" className="cursor-pointer">
                      <SlidersHorizontal className="mr-2 h-4 w-4" />
                      {t("nav.meetingSettings")}
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                      {user?.full_name?.charAt(0).toUpperCase() || "U"}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {user?.full_name || t("common.user")}
                      </p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user?.email}
                      </p>
                      {tenant && (
                        <p className="text-xs leading-none text-muted-foreground">
                          {tenant.company_name}
                        </p>
                      )}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/account" className="cursor-pointer">
                      <UserCog className="mr-2 h-4 w-4" />
                      {t("nav.accountSettings")}
                    </Link>
                  </DropdownMenuItem>
                  {canManageDeveloper && (
                    <DropdownMenuItem asChild>
                      <Link to="/developer/api-keys" className="cursor-pointer">
                        <KeyRound className="mr-2 h-4 w-4" />
                        {t("nav.developerApi")}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {canManageCompany && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/company/users" className="cursor-pointer">
                          <Users className="mr-2 h-4 w-4" />
                          {t("nav.companyUsers")}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/company/settings" className="cursor-pointer">
                          <Building2 className="mr-2 h-4 w-4" />
                          {t("nav.companySettings")}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/company/roles" className="cursor-pointer">
                          <SlidersHorizontal className="mr-2 h-4 w-4" />
                          {t("nav.rolesLimits")}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/company/approvals" className="cursor-pointer">
                          <FileCheck className="mr-2 h-4 w-4" />
                          {t("nav.approvals")}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/company/usage" className="cursor-pointer">
                          <Gauge className="mr-2 h-4 w-4" />
                          {t("nav.companyUsage")}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/company/billing" className="cursor-pointer">
                          <CreditCard className="mr-2 h-4 w-4" />
                          {t("nav.billing")}
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  {user?.is_admin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="cursor-pointer">
                          <Settings className="mr-2 h-4 w-4" />
                          {t("nav.websiteSettings")}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/admin/companies" className="cursor-pointer">
                          <Building2 className="mr-2 h-4 w-4" />
                          {t("nav.companies")}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/admin/credits" className="cursor-pointer">
                          <Coins className="mr-2 h-4 w-4" />
                          Credits
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/admin/users" className="cursor-pointer">
                          <Users className="mr-2 h-4 w-4" />
                          {t("nav.userManagement")}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/admin/trial-settings" className="cursor-pointer">
                          <SlidersHorizontal className="mr-2 h-4 w-4" />
                          Trial Settings
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    {t("common.signOut")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button variant="ghost" className="text-muted-foreground hover:text-foreground font-medium" asChild>
                <Link to="/login">{t("common.signIn")}</Link>
              </Button>
              {!tenantSubdomain ? (
                <Button className="gradient-ai font-medium" asChild>
                  <Link to="/register">{t("auth.startFreeTrial")}</Link>
                </Button>
              ) : null}
            </>
          )}
          </div>
        </div>
      </nav>
  );
};
